"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { collection, doc, query, where, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthContext } from "./AuthContext";
import { fetchDocument } from "@/lib/firestore";
import type { MemberProfile, Household } from "@/lib/models";

const HOUSEHOLD_KEY = "zoey_selected_household";

interface HouseholdContextValue {
  household: Household | null;
  householdId: string | null;
  allHouseholds: Household[];
  members: MemberProfile[];
  profile: MemberProfile | null;
  loading: boolean;
  switchHousehold: (id: string) => void;
  /**
   * Force a fresh fetch of the active household + its members. Use this from
   * mutation flows (Add Pet, accept invite, etc.) to guarantee the UI reflects
   * the write immediately, in case the snapshot listener delivery is delayed
   * or missed for any reason.
   */
  refreshHousehold: () => Promise<void>;
}

const HouseholdContext = createContext<HouseholdContextValue | null>(null);

export function HouseholdProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [household, setHousehold] = useState<Household | null>(null);
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [allHouseholds, setAllHouseholds] = useState<Household[]>([]);
  const [members, setMembers] = useState<MemberProfile[]>([]);
  const [loading, setLoading] = useState(true);

  // Load user profile
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setHousehold(null);
      setHouseholdId(null);
      setAllHouseholds([]);
      setMembers([]);
      setLoading(false);
      return;
    }

    const ref = collection(db, "member_profiles");
    const q = query(ref, where("authUid", "==", user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const p = { ...doc.data(), uid: doc.id } as MemberProfile;
        setProfile(p);
      }
    });
    return unsubscribe;
  }, [user]);

  // Load households when profile changes
  useEffect(() => {
    // CRITICAL: do NOT setLoading(false) while profile is still null. The
    // profile snapshot listener (effect above) hasn't fired yet on initial
    // mount; setting loading=false here would make the layout's HouseholdGate
    // see `hasNoHouseholds === true` (loading=false + profile=null), redirect
    // to /onboarding, which then redirects to /dashboard. That bug bites on
    // any hard navigation into a (app) route (e.g., /chats/{id} which falls
    // back to a full page load because Next.js can't find the RSC payload
    // for an unknown chatId in the static export).
    if (profile === null) return;

    if (!profile.households?.length) {
      setAllHouseholds([]);
      setHouseholdId(null);
      setLoading(false);
      return;
    }

    async function loadHouseholds() {
      const hhs: Household[] = [];
      for (const hhId of profile!.households!) {
        const hh = await fetchDocument<Household>("households", hhId);
        if (hh) hhs.push(hh);
      }
      setAllHouseholds(hhs);

      // Determine active household
      const persisted = localStorage.getItem(HOUSEHOLD_KEY);
      const validPersisted = persisted && profile!.households!.includes(persisted);
      const activeId = validPersisted ? persisted : profile!.households![0];
      setHouseholdId(activeId);
    }

    loadHouseholds();
  }, [profile]);

  // Load active household data and members via real-time snapshot listeners.
  // Without listeners, mutations elsewhere in the app (Add Pet, accept QR
  // invite, leave household, edit member nickname/avatar) wouldn't propagate
  // until the user reloads. The household doc listener triggers a refresh of
  // the per-member listeners whenever `household_members` changes.
  useEffect(() => {
    if (!householdId) {
      setHousehold(null);
      setMembers([]);
      return;
    }

    const memberUnsubs = new Map<string, () => void>();

    const unsubHousehold = onSnapshot(
      doc(db, "households", householdId),
      (snap) => {
        if (!snap.exists()) {
          setHousehold(null);
          setMembers([]);
          memberUnsubs.forEach((u) => u());
          memberUnsubs.clear();
          setLoading(false);
          return;
        }

        const hh = { ...snap.data(), uid: snap.id } as Household;
        setHousehold(hh);

        const memberIds = hh.household_members ?? [];
        const idSet = new Set(memberIds);

        // Tear down listeners for members no longer in the household.
        for (const [id, unsub] of memberUnsubs) {
          if (!idSet.has(id)) {
            unsub();
            memberUnsubs.delete(id);
            setMembers((prev) => prev.filter((m) => m.uid !== id));
          }
        }

        // Spin up listeners for newly added members (or pets).
        for (const memberId of memberIds) {
          if (memberUnsubs.has(memberId)) continue;
          const unsubMember = onSnapshot(
            doc(db, "member_profiles", memberId),
            (memberSnap) => {
              if (!memberSnap.exists()) {
                setMembers((prev) => prev.filter((m) => m.uid !== memberId));
                return;
              }
              const m = {
                ...memberSnap.data(),
                uid: memberSnap.id,
              } as MemberProfile;
              setMembers((prev) => {
                const idx = prev.findIndex((x) => x.uid === memberId);
                if (idx === -1) return [...prev, m];
                const next = prev.slice();
                next[idx] = m;
                return next;
              });
            },
          );
          memberUnsubs.set(memberId, unsubMember);
        }

        setLoading(false);
      },
    );

    return () => {
      unsubHousehold();
      memberUnsubs.forEach((u) => u());
      memberUnsubs.clear();
    };
  }, [householdId]);

  const switchHousehold = useCallback((id: string) => {
    setHouseholdId(id);
    localStorage.setItem(HOUSEHOLD_KEY, id);
  }, []);

  // Manual fallback for callers that just performed a write and want a
  // synchronous refresh, without waiting for the snapshot listener (which
  // may be delayed by Firestore's offline cache, network conditions, or
  // strict-mode listener teardown). Reads the household doc and fans out
  // to a getDoc per member, then writes the result into local state.
  const refreshHousehold = useCallback(async () => {
    if (!householdId) return;
    const hhSnap = await getDoc(doc(db, "households", householdId));
    if (!hhSnap.exists()) return;
    const hh = { ...hhSnap.data(), uid: hhSnap.id } as Household;
    setHousehold(hh);

    const memberIds = hh.household_members ?? [];
    const memberDocs = await Promise.all(
      memberIds.map((id) => getDoc(doc(db, "member_profiles", id))),
    );
    const memberProfiles: MemberProfile[] = [];
    for (const m of memberDocs) {
      if (m.exists()) {
        memberProfiles.push({ ...m.data(), uid: m.id } as MemberProfile);
      }
    }
    setMembers(memberProfiles);
  }, [householdId]);

  return (
    <HouseholdContext.Provider
      value={{
        household,
        householdId,
        allHouseholds,
        members,
        profile,
        loading,
        switchHousehold,
        refreshHousehold,
      }}
    >
      {children}
    </HouseholdContext.Provider>
  );
}

export function useHouseholdContext() {
  const context = useContext(HouseholdContext);
  if (!context)
    throw new Error("useHouseholdContext must be used within HouseholdProvider");
  return context;
}
